import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FileIcons from './FileIcons';

type NodeFn<T> = (item: T) => React.ReactNode;

export interface DataCardField<T> {
    label: string;
    render: NodeFn<T>;
    hideIf?: (item: T) => boolean;
}

export interface DataCardFile<T> {
    label: string;
    id: string;
    hideIf?: (item: T) => boolean;
}

export interface DataCardProps<T> {
    item: T;

    // Header band
    leftBadgeNode?: NodeFn<T>; // e.g., renderBooleanBadge(...)
    rightMeta?: (item: T) => { label: string; value: React.ReactNode } | null;

    // Title + actions
    title: (item: T) => string;
    subtitle?: NodeFn<T>;
    headerActions?: NodeFn<T>;

    // Optional media region (banner/image)
    media?: NodeFn<T>;

    // Body
    fields: Array<DataCardField<T>>;
    files?: Array<DataCardFile<T>>;

    // Buttons
    primaryActions?: NodeFn<T>;
    secondaryActions?: NodeFn<T>;

    // Styling
    className?: string;
    withDivider?: boolean; // adds a subtle divider under the header area
    collapsibleOnMobile?: boolean; // enables collapse/expand on mobile (default false)
}

const DataCard = <T,>({
    item,
    leftBadgeNode,
    rightMeta,
    title,
    subtitle,
    headerActions,
    media,
    fields,
    files = [],
    primaryActions,
    secondaryActions,
    className = '',
    withDivider = true,
    collapsibleOnMobile = false,
}: DataCardProps<T>) => {
    const meta = rightMeta?.(item);
    const [isCollapsed, setIsCollapsed] = useState(true);

    return (
        <div
            className={[
                // container
                'bg-white dark:bg-slate-800 rounded-2xl',
                'shadow-sm ring-1 ring-slate-200 dark:ring-slate-700',
                'h-full flex flex-col',
                className,
            ].join(' ')}
        >
            <div className="px-6 pt-5 flex-1">
                {/* Header band: left badge + right meta */}
                {(leftBadgeNode || meta) && (
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            {leftBadgeNode?.(item)}
                        </div>
                        {meta && (
                            <div className="text-xs text-slate-500">
                                <span className="font-medium">{meta.label}:</span>{' '}
                                <span className="text-slate-700 dark:text-slate-300">{meta.value}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Title row with optional header actions and mobile toggle */}
                <div className="flex items-start justify-between gap-3">
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                        {title(item)}
                    </h3>
                    <div className="flex items-center gap-2">
                        {headerActions && (
                            <div className="flex-shrink-0">{headerActions(item)}</div>
                        )}
                        {collapsibleOnMobile && (
                            <button
                                onClick={() => setIsCollapsed(!isCollapsed)}
                                className="md:hidden flex-shrink-0 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                                aria-label={isCollapsed ? 'Expand details' : 'Collapse details'}
                            >
                                {isCollapsed ? (
                                    <ChevronDown className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                ) : (
                                    <ChevronUp className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {subtitle && (
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {subtitle(item)}
                    </div>
                )}

                {/* Optional media - always visible */}
                {media && <div className="mt-4">{media(item)}</div>}

                {withDivider && (
                    <div className="mt-4 border-t border-slate-200 dark:border-slate-700" />
                )}

                {/* Details - hidden on mobile when collapsed */}
                <div className={`mt-4 flex-1 ${collapsibleOnMobile && isCollapsed ? 'hidden md:block' : ''}`}>
                    <dl className="grid grid-cols-1 gap-y-2">

                        {fields.map((f, i) =>
                            f.hideIf?.(item) ? null : (
                                <div key={i} className="flex flex-col">
                                    <dt className="text-xs text-slate-500">{f.label}:</dt>
                                    <dd className="text-xs text-slate-800 dark:text-slate-200">
                                        {f.render(item)}
                                    </dd>
                                </div>
                            ),
                        )}

                        {files.map((file, i) => {
                            if (file.hideIf?.(item)) return null;
                            return (
                                <div key={`file-${i}`} className="flex flex-col">
                                    <dt className="text-xs text-slate-500">{file.label}:</dt>
                                    <dd>
                                        <FileIcons
                                            files={[
                                                {
                                                    id: file.id,
                                                    label: file.label
                                                }
                                            ]}
                                        />
                                    </dd>
                                </div>
                            );
                        })}
                    </dl>
                </div>
            </div>

            {/* Buttons - hidden on mobile when collapsed */}
            {(primaryActions || secondaryActions) && (
                <div className={`px-6 pb-6 mt-auto ${collapsibleOnMobile && isCollapsed ? 'hidden md:block' : ''}`}>
                    {primaryActions && (
                        <div className="mt-6 flex flex-wrap gap-3">{primaryActions(item)}</div>
                    )}
                    {secondaryActions && (
                        <div className="mt-3 flex flex-col gap-3">{secondaryActions(item)}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DataCard;
